import { ReactNode } from 'react';
import styled from 'styled-components';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/plus-2.svg';
import { ReactComponent as VIcon } from '@elementary/common-ui/assets/v-green.svg';
import {
  FlexColumn,
  Flex,
  Button,
  FlexCenter,
} from '@elementary/common-ui/components';
import Tooltip from '@mui/material/Tooltip';
import { Typography, CircularProgress } from '@mui/material';
import {
  fontWeight,
  lineHeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';
import { getIllustrationRules } from '@elementary/common-ui/style/iconColors';

const IllustrationTile = styled(FlexCenter)`
  svg {
    visibility: visible;
  }

  ${({ theme }) => getIllustrationRules(theme.tokens.components.illustration)}
`;

const Wrapper = styled(Flex)`
  min-height: 112px;
  height: fit-content;
  width: 100%;
  background: ${({ theme }) => theme.palette.surface.base};
  border: 1px solid ${({ theme }) => theme.palette.border.light};
  border-radius: ${radius('lg')};
  align-items: center;
  justify-content: space-between;
`;

const DetailsSection = styled(Flex)`
  padding: ${space('xxl')};
  gap: ${space('xxl')};
  align-items: start;
  flex: 1;
`;

const ActionsSection = styled(Flex)`
  align-items: center;
  padding: ${space('xxl')};
`;

const Spinner = styled(CircularProgress)`
  color: ${({ theme }) => theme.palette.content.subtle};
`;

const ConnectedIndicator = () => (
  <Flex sx={{ gap: space('smMinus'), alignItems: 'center' }}>
    <VIcon width={18} />
    <Typography
      sx={{ color: 'status.success.main', fontWeight: fontWeight('semibold') }}
    >
      Connected
    </Typography>
  </Flex>
);

type Props = {
  title: string;
  description: string;
  info?: string;
  addText?: string;
  icon?: ReactNode;
  connected?: boolean;
  loading?: boolean;
  onOpen: () => void;
  disabled?: boolean;
  connectTooltipTitle?: ReactNode;
  supportMultipleDestinations?: boolean;
  multipleDestinationsRenderer?: () => ReactNode;
  onMultipleDestinationsConnectClick?: () => void;
  connectedButtonText?: string;
  hideAddButton?: boolean;
};

const IntegrationBox = ({
  title,
  description,
  info,
  addText,
  icon,
  connected,
  loading,
  onOpen,
  disabled,
  connectTooltipTitle,
  supportMultipleDestinations,
  multipleDestinationsRenderer,
  onMultipleDestinationsConnectClick,
  connectedButtonText = 'Edit',
  hideAddButton = false,
}: Props) => (
  <Wrapper>
    <DetailsSection>
      {icon && <IllustrationTile>{icon}</IllustrationTile>}
      <FlexColumn sx={{ gap: 1, flex: 1 }}>
        <Flex sx={{ gap: 2, height: '20px' }}>
          <Typography
            sx={{
              fontWeight: fontWeight('semibold'),
              lineHeight: lineHeight('md'),
            }}
          >
            {title}
          </Typography>
          {connected && !supportMultipleDestinations && <ConnectedIndicator />}
        </Flex>
        <Typography
          sx={{ lineHeight: lineHeight('sm'), color: 'content.secondary' }}
        >
          {description}
        </Typography>
        {info && (
          <Typography variant="caption" sx={{ color: 'content.secondary' }}>
            {info}
          </Typography>
        )}
        {supportMultipleDestinations && multipleDestinationsRenderer && (
          <FlexColumn sx={{ mt: 3, gap: space('xxl') }}>
            {multipleDestinationsRenderer()}
            {!hideAddButton && (
              <Tooltip
                title={connectTooltipTitle}
                placement="top-start"
                disableHoverListener={!connectTooltipTitle}
              >
                <span style={{ display: 'inline-flex', width: 'fit-content' }}>
                  <Button
                    sx={{ width: 'fit-content' }}
                    variant="text"
                    color="secondary"
                    onClick={onMultipleDestinationsConnectClick}
                    startIcon={<PlusIcon />}
                  >
                    {addText}
                  </Button>
                </span>
              </Tooltip>
            )}
          </FlexColumn>
        )}
      </FlexColumn>
    </DetailsSection>
    {!supportMultipleDestinations && (
      <ActionsSection sx={{ gap: 2 }}>
        {loading && (
          <Flex sx={{ gap: 1, alignItems: 'center' }}>
            <Spinner size={14} thickness={5} />
            <Typography
              noWrap
              sx={{
                color: 'content.subtle',
                fontWeight: fontWeight('semibold'),
              }}
            >
              Loading data
            </Typography>
          </Flex>
        )}
        {connected ? (
          <Button variant="outlined" onClick={() => onOpen()}>
            {connectedButtonText}
          </Button>
        ) : (
          <Tooltip
            title={connectTooltipTitle}
            placement="top-start"
            disableHoverListener={!connectTooltipTitle}
          >
            <span style={{ display: 'inline-flex' }}>
              <Button disabled={disabled} onClick={() => onOpen()}>
                Connect
              </Button>
            </span>
          </Tooltip>
        )}
      </ActionsSection>
    )}
  </Wrapper>
);

export default IntegrationBox;
